import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Thumbs, FreeMode } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/thumbs";
import "swiper/css/free-mode";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

interface System {
  _id: string;
  name: string;
  description: string;
  tagline?: string;
  content?: string;
  imageUrl: string;
  images?: string[];
}

const resolveImageUrl = (url: string) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${API_URL}${url.startsWith("/") ? "" : "/"}${url}`;
};

const getGallery = (system: System) => {
  const list =
    system.images && system.images.length > 0
      ? system.images
      : system.imageUrl
      ? [system.imageUrl]
      : [];
  return Array.from(new Set(list.filter(Boolean)));
};

/** Renders simple markdown-like content (paragraphs, headings, bullet lists). */
const SystemContent: React.FC<{ content: string }> = ({ content }) => {
  const blocks = useMemo(() => {
    const lines = content.replace(/\r\n/g, "\n").split("\n");
    const result: Array<
      | { type: "heading"; text: string }
      | { type: "paragraph"; text: string }
      | { type: "list"; items: string[] }
    > = [];

    let paragraph: string[] = [];
    let listItems: string[] = [];

    const flushParagraph = () => {
      if (paragraph.length) {
        result.push({ type: "paragraph", text: paragraph.join(" ").trim() });
        paragraph = [];
      }
    };

    const flushList = () => {
      if (listItems.length) {
        result.push({ type: "list", items: [...listItems] });
        listItems = [];
      }
    };

    const isHeading = (line: string) => {
      if (line.startsWith("*") || line.startsWith("-")) return false;
      if (line.length > 80) return false;
      if (/[.!?]$/.test(line)) return false;
      // Section titles are typically Title Case / short phrases
      return /^[A-ZÇĞİÖŞÜa-zçğıöşü0-9]/.test(line) && line.split(" ").length <= 10;
    };

    for (const raw of lines) {
      const line = raw.trim();
      if (!line) {
        flushParagraph();
        flushList();
        continue;
      }

      if (line.startsWith("* ") || line.startsWith("- ")) {
        flushParagraph();
        listItems.push(line.replace(/^[\*\-]\s+/, "").replace(/,$/, ""));
        continue;
      }

      // Heuristic: short non-sentence lines after blank = heading
      if (
        paragraph.length === 0 &&
        listItems.length === 0 &&
        isHeading(line) &&
        !line.includes(",")
      ) {
        flushParagraph();
        flushList();
        result.push({ type: "heading", text: line });
        continue;
      }

      flushList();
      paragraph.push(line);
    }

    flushParagraph();
    flushList();
    return result;
  }, [content]);

  return (
    <div className="space-y-5 text-gray-700 leading-relaxed">
      {blocks.map((block, index) => {
        if (block.type === "heading") {
          return (
            <h2
              key={index}
              className="text-xl md:text-2xl font-bold text-gray-900 pt-4"
            >
              {block.text}
            </h2>
          );
        }
        if (block.type === "list") {
          return (
            <ul key={index} className="list-disc pl-6 space-y-2">
              {block.items.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          );
        }
        return (
          <p key={index} className="text-base md:text-lg">
            {block.text}
          </p>
        );
      })}
    </div>
  );
};

const SystemDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [system, setSystem] = useState<System | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType | null>(null);

  useEffect(() => {
    if (!id) return;
    const fetchSystem = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${API_URL}/api/admin/systems/public/${id}`
        );
        setSystem(response.data.system);
        setError("");
      } catch (err: any) {
        setError(
          err?.response?.data?.message ||
            "Sistem detayı yüklenirken bir hata oluştu"
        );
      } finally {
        setLoading(false);
      }
    };
    fetchSystem();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-primary-600"></div>
      </div>
    );
  }

  if (error || !system) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-4">
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 max-w-lg w-full">
          <p>{error || "Sistem bulunamadı"}</p>
        </div>
        <Link to="/sistemler" className="text-primary-700 font-medium hover:underline">
          Sistemlere dön
        </Link>
      </div>
    );
  }

  const gallery = getGallery(system);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-primary-600 to-primary-800 py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={() => navigate("/sistemler")}
            className="text-white/80 hover:text-white text-sm mb-4 inline-flex items-center"
          >
            ← Tüm sistemler
          </button>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-3">
            {system.name}
          </h1>
          {(system.tagline || system.description) && (
            <p className="text-lg md:text-xl text-white/90 max-w-3xl">
              {system.tagline || system.description}
            </p>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
          <div className="lg:col-span-3">
            {gallery.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <Swiper
                  modules={[Navigation, Pagination, Thumbs]}
                  navigation
                  pagination={{ clickable: true }}
                  thumbs={{
                    swiper:
                      thumbsSwiper && !thumbsSwiper.destroyed
                        ? thumbsSwiper
                        : null,
                  }}
                  className="system-gallery-swiper"
                  style={{ minHeight: "320px" }}
                >
                  {gallery.map((img, index) => (
                    <SwiperSlide key={`${system._id}-main-${index}`}>
                      <img
                        src={resolveImageUrl(img)}
                        alt={`${system.name} ${index + 1}`}
                        className="w-full h-[320px] md:h-[480px] object-cover"
                      />
                    </SwiperSlide>
                  ))}
                </Swiper>

                {gallery.length > 1 && (
                  <div className="p-3 border-t border-gray-100">
                    <Swiper
                      onSwiper={setThumbsSwiper}
                      modules={[FreeMode, Thumbs]}
                      freeMode
                      watchSlidesProgress
                      spaceBetween={10}
                      slidesPerView={Math.min(gallery.length, 5)}
                      className="system-thumbs-swiper"
                    >
                      {gallery.map((img, index) => (
                        <SwiperSlide
                          key={`${system._id}-thumb-${index}`}
                          className="cursor-pointer"
                        >
                          <img
                            src={resolveImageUrl(img)}
                            alt=""
                            className="w-full h-16 md:h-20 object-cover rounded-md opacity-70 hover:opacity-100 transition"
                          />
                        </SwiperSlide>
                      ))}
                    </Swiper>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 sticky top-24">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                Bu sistem hakkında
              </h2>
              <p className="text-gray-600 mb-6">
                {system.description}
              </p>
              <p className="text-sm text-gray-500 mb-6">
                {gallery.length} görsel
              </p>
              <button
                type="button"
                onClick={() =>
                  navigate(
                    `/iletisim?system=${encodeURIComponent(system.name)}`
                  )
                }
                className="w-full bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-semibold py-3 px-6 rounded-lg transition-all"
              >
                Teklif İste
              </button>
            </div>
          </div>
        </div>

        {system.content && (
          <div className="mt-10 md:mt-14 bg-white rounded-2xl shadow-lg p-6 md:p-10">
            <SystemContent content={system.content} />
          </div>
        )}
      </div>
    </div>
  );
};

export default SystemDetailPage;
